import { useEffect } from "react";
import { ASSETS } from "../lib/constants";
import "./Cards.css";

const HERO_PNG = "https://customer-assets.emergentagent.com/job_trt-plus-app/artifacts/0ocv2oaf_Hero.PNG";
const PK_BG = "https://customer-assets.emergentagent.com/job_trt-plus-app/artifacts/sqsdifcx_Background.JPG";

/* Hidden preview route for store-listing card prototypes.
   Each <StoreCard /> renders at its native pixel dimensions inside a
   visually-scaled wrapper. Use Chrome DevTools "Capture node screenshot"
   on the inner [data-card] element, or run the Playwright export
   script at /scripts/export-cards.mjs to dump every card to PNG. */
export default function Cards() {
    useEffect(() => {
        document.title = "Cards · TRT Plus (preview)";
    }, []);

    return (
        <main
            className="cards-page"
            data-testid="cards-page"
        >
            <h1>Store-listing card prototypes</h1>
            <p className="cards-page-intro">
                Each card below renders at its <strong>native target
                resolution</strong>. The on-screen preview is scaled down for
                comfort, but the underlying box is full-size — so a Chrome
                DevTools <kbd>Capture node screenshot</kbd> on a card's
                inner element exports a pixel-perfect PNG at full size
                regardless of monitor. For batch export, run the
                Playwright script at <kbd>/scripts/export-cards.mjs</kbd>.
            </p>

            <h2 style={{ fontFamily: "var(--font-display)", marginTop: 24, marginBottom: 18, fontSize: 18, color: "#fff" }}>
                Open Graph cards · 1200 × 630
            </h2>
            <p className="cards-page-intro" style={{ marginBottom: 24 }}>
                Three OG card design variants. Pick one, capture node screenshot, save as <kbd>/app/frontend/public/og-image.jpg</kbd>.
            </p>
            <div className="cards-grid">
                <StoreCard label="OG · Variant A — Hero front" targetW={1200} targetH={630} previewW={480}>
                    <OgVariantA />
                </StoreCard>
                <StoreCard label="OG · Variant B — Editorial split" targetW={1200} targetH={630} previewW={480}>
                    <OgVariantB />
                </StoreCard>
                <StoreCard label="OG · Variant C — Telemetry" targetW={1200} targetH={630} previewW={480}>
                    <OgVariantC />
                </StoreCard>
            </div>

            <h2 style={{ fontFamily: "var(--font-display)", marginTop: 48, marginBottom: 18, fontSize: 18, color: "#fff" }}>
                Store cards
            </h2>
            <div className="cards-grid">
                <StoreCard
                    label="App Store · iPhone 6.7″"
                    targetW={1290}
                    targetH={2796}
                    previewW={300}
                >
                    <BloodworkCardContent />
                </StoreCard>

                <StoreCard
                    label="Google Play · Phone (9:16)"
                    targetW={1080}
                    targetH={1920}
                    previewW={300}
                >
                    <BloodworkCardContent variant="play" />
                </StoreCard>
            </div>
        </main>
    );
}

/* Visually-scaled wrapper around a native-size card. Returns a
   labelled frame so the user knows the format + dimensions of each.
   The inner card stays at full resolution for export. */
function StoreCard({ label, targetW, targetH, previewW, children }) {
    const scale = previewW / targetW;
    const wrapH = targetH * scale;
    return (
        <div className="card-frame" data-card-frame={`${targetW}x${targetH}`}>
            <div className="card-frame-meta">
                {label} · {targetW}×{targetH}
            </div>
            <div
                className="card-scale-wrap"
                style={{ width: previewW, height: wrapH }}
            >
                <div
                    className="card-scale-inner"
                    style={{
                        transform: `scale(${scale})`,
                        width: targetW,
                        height: targetH,
                    }}
                >
                    <div
                        className="store-card"
                        data-card={`${targetW}x${targetH}`}
                        style={{ width: targetW, height: targetH }}
                    >
                        <div className="store-card-bg store-card-bg-grid" />
                        <div className="store-card-bg store-card-bg-glow" />
                        <div className="store-card-bg store-card-bg-vignette" />
                        <div className="store-card-content">{children}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────
   Open Graph variants — 1200 × 630.
   Shared design language: pure black base, faint grid, faded PK
   formulas drifting in the back layer, floating Hero device PNG, and
   the Audiowide "TRT+" wordmark. Each variant explores a different
   composition while staying inside the clinical/athletic palette
   (#000, #fff, #2BA6E0, #00E6A0).
   ──────────────────────────────────────────────────────────────── */

/* Drifting faded formulas background.
   Four formula PNGs from the engine schematic, scattered + rotated at
   low opacity to read as "scientific wallpaper" without competing with
   the foreground. */
function OgFormulaWall({ intensity = 0.14 }) {
    const formulas = [
        { src: ASSETS.formulaZakharov,    top:  "6%",  left:  "2%",  w: 520, rot: -4,  o: intensity * 1.0 },
        { src: ASSETS.formulaAERatio,     top: "62%",  left:  "1%",  w: 360, rot:  2,  o: intensity * 0.95 },
        { src: ASSETS.formulaCream,       top: "10%",  left: "62%",  w: 480, rot:  3,  o: intensity * 0.9 },
        { src: ASSETS.formulaMultiRoute,  top: "58%",  left: "55%",  w: 560, rot: -2,  o: intensity * 1.0 },
    ];
    return (
        <div
            aria-hidden
            style={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 0,
            }}
        >
            {formulas.map((f, i) => (
                <img
                    key={i}
                    src={f.src}
                    alt=""
                    style={{
                        position: "absolute",
                        top: f.top,
                        left: f.left,
                        width: f.w,
                        opacity: f.o,
                        transform: `rotate(${f.rot}deg)`,
                        filter: "grayscale(1) brightness(2.4) contrast(0.9)",
                        mixBlendMode: "screen",
                    }}
                />
            ))}
        </div>
    );
}

/* Floating hero phone image with electric-blue ground glow. */
function OgHeroPhone({ size = 560, style = {} }) {
    return (
        <div
            style={{
                position: "relative",
                width: size,
                height: size * 1.05,
                ...style,
            }}
        >
            {/* Mint accent ring under device */}
            <div
                style={{
                    position: "absolute",
                    left: "50%",
                    bottom: "4%",
                    transform: "translateX(-50%)",
                    width: size * 0.72,
                    height: size * 0.08,
                    background:
                        "radial-gradient(ellipse at center, rgba(0,230,160,0.55) 0%, rgba(0,230,160,0) 70%)",
                    filter: "blur(6px)",
                }}
            />
            {/* Electric-blue main glow */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(circle at 50% 55%, rgba(43,166,224,0.55) 0%, rgba(43,166,224,0.12) 40%, transparent 70%)",
                    filter: "blur(20px)",
                }}
            />
            <img
                src={HERO_PNG}
                alt="TRT Plus app"
                style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    filter:
                        "drop-shadow(0 30px 80px rgba(0,0,0,0.75)) drop-shadow(0 0 60px rgba(43,166,224,0.45))",
                }}
            />
        </div>
    );
}

/* Wordmark — Audiowide "TRT+" with mint plus. */
function OgWordmark({ size = 88, sub = true }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
                style={{
                    fontFamily: "var(--font-brand)",
                    fontSize: size,
                    lineHeight: 0.9,
                    color: "#fff",
                    letterSpacing: "0.02em",
                    textShadow: "0 0 32px rgba(43,166,224,0.45)",
                }}
            >
                TRT<span style={{ color: "#2BA6E0" }}>+</span>
            </div>
            {sub && (
                <div
                    style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 14,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.55)",
                    }}
                >
                    Clinical · Athletic · Personal
                </div>
            )}
        </div>
    );
}

/* Variant A — Hero front.
   Centred device, formulas drifting behind, headline above, brand
   wordmark + URL below. Most "magazine cover" of the three. */
function OgVariantA() {
    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                background: "#000",
                overflow: "hidden",
            }}
        >
            <OgFormulaWall intensity={0.16} />

            {/* Eyebrow — top-left */}
            <div
                style={{
                    position: "absolute",
                    top: 44,
                    left: 56,
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.72)",
                    zIndex: 2,
                }}
            >
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "#00E6A0",
                        boxShadow: "0 0 18px rgba(0,230,160,0.85)",
                    }}
                />
                Hormone optimization engine
            </div>

            {/* Wordmark — top-right */}
            <div style={{ position: "absolute", top: 36, right: 56, zIndex: 2, textAlign: "right" }}>
                <OgWordmark size={64} sub={false} />
            </div>

            {/* Headline + device row */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    gridTemplateColumns: "1.05fr 1fr",
                    alignItems: "center",
                    padding: "0 56px",
                    zIndex: 2,
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 22, paddingRight: 24 }}>
                    <h2
                        style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 700,
                            fontSize: 84,
                            lineHeight: 0.98,
                            letterSpacing: "-0.02em",
                            color: "#fff",
                            margin: 0,
                        }}
                    >
                        The math
                        <br />
                        behind your
                        <br />
                        <span style={{ color: "#2BA6E0" }}>protocol.</span>
                    </h2>
                    <p
                        style={{
                            fontFamily: "var(--font-body)",
                            fontSize: 22,
                            lineHeight: 1.45,
                            color: "rgba(255,255,255,0.62)",
                            margin: 0,
                            maxWidth: 520,
                        }}
                    >
                        Pharmacokinetic modeling, calibrated bloodwork
                        and composite scoring — engineered for men who
                        treat optimization like a discipline.
                    </p>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    <OgHeroPhone size={520} />
                </div>
            </div>

            {/* Footer URL */}
            <div
                style={{
                    position: "absolute",
                    bottom: 36,
                    left: 56,
                    right: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.45)",
                    zIndex: 2,
                }}
            >
                <span>trtplus.app</span>
                <span style={{ color: "#00E6A0" }}>iOS · Android</span>
            </div>
        </div>
    );
}

/* Variant B — Editorial split.
   Left column: large Audiowide TRT+ stacked with subhead. Right
   column: floating hero with formulas tighter around it. Hairline
   divider between for a hard editorial feel. */
function OgVariantB() {
    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                background: "#000",
                overflow: "hidden",
            }}
        >
            <OgFormulaWall intensity={0.11} />

            {/* Hairline divider */}
            <div
                style={{
                    position: "absolute",
                    top: 60,
                    bottom: 60,
                    left: "48%",
                    width: 1,
                    background:
                        "linear-gradient(180deg, transparent, rgba(255,255,255,0.18) 30%, rgba(255,255,255,0.18) 70%, transparent)",
                    zIndex: 2,
                }}
            />

            {/* Left column */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: "48%",
                    padding: "56px 56px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    zIndex: 3,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.7)",
                    }}
                >
                    <span
                        style={{
                            width: 7,
                            height: 7,
                            borderRadius: 999,
                            background: "#00E6A0",
                            boxShadow: "0 0 16px rgba(0,230,160,0.85)",
                        }}
                    />
                    TRT Plus · est. 2015
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
                    <div
                        style={{
                            fontFamily: "var(--font-brand)",
                            fontSize: 188,
                            lineHeight: 0.85,
                            color: "#fff",
                            letterSpacing: "0.02em",
                            textShadow:
                                "0 0 50px rgba(43,166,224,0.4), 0 0 12px rgba(43,166,224,0.25)",
                        }}
                    >
                        TRT<span style={{ color: "#2BA6E0" }}>+</span>
                    </div>
                    <div
                        style={{
                            fontFamily: "var(--font-display)",
                            fontSize: 30,
                            lineHeight: 1.18,
                            letterSpacing: "-0.01em",
                            color: "rgba(255,255,255,0.78)",
                            margin: 0,
                            maxWidth: 440,
                        }}
                    >
                        Pharmacokinetic modeling for
                        <br />
                        hormone optimization.
                    </div>
                </div>

                <div
                    style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.45)",
                    }}
                >
                    trtplus.app
                </div>
            </div>

            {/* Right column — floating hero */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    right: 0,
                    width: "52%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                }}
            >
                <OgHeroPhone size={520} />
            </div>

            {/* Corner crosshair tick — engineering motif */}
            <div
                style={{
                    position: "absolute",
                    bottom: 30,
                    right: 36,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.32)",
                    zIndex: 3,
                }}
            >
                v · 04 / 1200 × 630
            </div>
        </div>
    );
}

/* Variant C — Telemetry.
   Formula wall slightly stronger. Floating hero on right. Left side
   has a stacked telemetry pill grid (BIO·AVAIL T / E2 / SHBG /
   PK·t½) — the most "diagnostic instrument" reading of the three. */
function OgVariantC() {
    const stats = [
        { label: "BIO·AVAIL T",   value: "742",   unit: "ng/dL", tone: "#2BA6E0" },
        { label: "E2 SENSITIVE", value: "28.4",  unit: "pg/mL", tone: "#00E6A0" },
        { label: "SHBG",           value: "31",    unit: "nmol/L", tone: "#fff" },
        { label: "PK · T½",       value: "4.4",   unit: "days",  tone: "#fff" },
    ];
    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                background: "#000",
                overflow: "hidden",
            }}
        >
            <OgFormulaWall intensity={0.18} />

            {/* Top bar */}
            <div
                style={{
                    position: "absolute",
                    top: 36,
                    left: 56,
                    right: 56,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    zIndex: 3,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.72)",
                    }}
                >
                    <span
                        style={{
                            width: 7,
                            height: 7,
                            borderRadius: 999,
                            background: "#00E6A0",
                            boxShadow: "0 0 16px rgba(0,230,160,0.85)",
                        }}
                    />
                    Telemetry · live build
                </div>

                <div
                    style={{
                        fontFamily: "var(--font-brand)",
                        fontSize: 52,
                        color: "#fff",
                        letterSpacing: "0.02em",
                        textShadow: "0 0 24px rgba(43,166,224,0.4)",
                    }}
                >
                    TRT<span style={{ color: "#2BA6E0" }}>+</span>
                </div>
            </div>

            {/* Body grid */}
            <div
                style={{
                    position: "absolute",
                    top: 130,
                    bottom: 110,
                    left: 56,
                    right: 56,
                    display: "grid",
                    gridTemplateColumns: "1.05fr 1fr",
                    gap: 32,
                    alignItems: "center",
                    zIndex: 3,
                }}
            >
                {/* Left — headline + telemetry pills */}
                <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                    <h2
                        style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 700,
                            fontSize: 64,
                            lineHeight: 1.0,
                            letterSpacing: "-0.02em",
                            color: "#fff",
                            margin: 0,
                            textTransform: "uppercase",
                        }}
                    >
                        Read the
                        <br />
                        <span style={{ color: "#2BA6E0" }}>relationships</span>
                        <br />
                        between markers.
                    </h2>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 14,
                            marginTop: 8,
                        }}
                    >
                        {stats.map((s) => (
                            <div
                                key={s.label}
                                style={{
                                    border: "1px solid rgba(255,255,255,0.14)",
                                    background:
                                        "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
                                    padding: "14px 18px",
                                    borderRadius: 8,
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 12,
                                        letterSpacing: "0.28em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.55)",
                                    }}
                                >
                                    {s.label}
                                </div>
                                <div
                                    style={{
                                        marginTop: 6,
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: 8,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontSize: 38,
                                            fontWeight: 700,
                                            color: s.tone,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {s.value}
                                    </span>
                                    <span
                                        style={{
                                            fontFamily: "var(--font-mono)",
                                            fontSize: 12,
                                            letterSpacing: "0.18em",
                                            textTransform: "uppercase",
                                            color: "rgba(255,255,255,0.55)",
                                        }}
                                    >
                                        {s.unit}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right — floating hero */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <OgHeroPhone size={460} />
                </div>
            </div>

            {/* Footer */}
            <div
                style={{
                    position: "absolute",
                    bottom: 36,
                    left: 56,
                    right: 56,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.45)",
                    zIndex: 3,
                }}
            >
                <span>trtplus.app</span>
                <span style={{ color: "#00E6A0" }}>iOS · Android · Engine v4</span>
            </div>
        </div>
    );
}

/* Bloodwork prototype content. Layout pattern A: eyebrow + headline
   anchored to the top, screenshot fills the lower half. Tuned for
   9:19.5 (App Store) and 9:16 (Google Play) aspect ratios using the
   same composition; the smaller viewport just shows less footroom. */
function BloodworkCardContent({ variant = "appstore" }) {
    const isPlay = variant === "play";
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                padding: isPlay ? "120px 96px" : "200px 110px",
                boxSizing: "border-box",
            }}
        >
            <div
                className="store-eyebrow"
                style={{ fontSize: isPlay ? 30 : 36 }}
            >
                My Bloodwork
            </div>

            <h2
                className="store-headline"
                style={{
                    fontSize: isPlay ? 130 : 160,
                    marginTop: isPlay ? 36 : 48,
                    maxWidth: "100%",
                }}
            >
                Performance
                <br />
                bloodwork.
                <br />
                <span className="blue-pop">Built for optimization.</span>
            </h2>

            <p
                className="store-tagline"
                style={{
                    fontSize: isPlay ? 36 : 44,
                    marginTop: isPlay ? 32 : 44,
                    maxWidth: isPlay ? 820 : 980,
                }}
            >
                Calibrated reference ranges. Composite scoring. Ratios
                that read the relationships between markers — not just
                isolated numbers.
            </p>

            <div
                style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    marginTop: isPlay ? 60 : 90,
                }}
            >
                <img
                    className="store-screenshot"
                    src={ASSETS.bloodworkScreen}
                    alt="My Bloodwork screen"
                    style={{
                        maxWidth: isPlay ? "78%" : "82%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        objectPosition: "bottom center",
                    }}
                />
            </div>

            <div
                className="store-brand"
                style={{
                    position: "absolute",
                    bottom: isPlay ? 56 : 80,
                    left: 0,
                    right: 0,
                    justifyContent: "center",
                    fontSize: isPlay ? 28 : 34,
                }}
            >
                <img
                    src={ASSETS.icon}
                    alt=""
                    style={{
                        width: isPlay ? 44 : 54,
                        height: isPlay ? 44 : 54,
                        borderRadius: 10,
                    }}
                />
                <span className="store-brand-mark">
                    TRT<span className="plus">+</span>
                </span>
            </div>
        </div>
    );
}
